import { z } from "zod";
import type { AgreementMemberInfo } from "../data/types";

const requiredString = (msg = "Required") => z.string().min(1, msg);

/** Required US phone: 10 digits or 1 + 10 digits */
const phoneRequired = z
  .string()
  .min(1, "Phone is required")
  .refine(
    (val) => {
      const d = (val || "").replace(/\D/g, "");
      return d.length === 10 || (d.length === 11 && d[0] === "1");
    },
    { message: "Enter a valid 10-digit phone number" }
  );

/** Schema for member agreement info (all fields required; used with react-hook-form). */
export const agreementMemberInfoSchema = z.object({
  fullName: requiredString("Full name is required"),
  companyName: requiredString("Business name is required"),
  date: requiredString("Date is required"),
  titlePosition: requiredString("Title / position is required"),
  address: requiredString("Address is required"),
  city: requiredString("City is required"),
  state: requiredString("State is required"),
  zipcode: requiredString("Zipcode is required"),
  phone: phoneRequired,
  ownerDriversLicense: requiredString("Owner(s) driver's license is required"),
  ownerSSN: z
    .string()
    .min(1, "Owner(s) SSN is required")
    .refine(
      (val) => (val || "").replace(/\D/g, "").length === 9,
      { message: "SSN must be 9 digits" }
    ),
  memberFEIN: requiredString("Member FEIN is required"),
  memberInsurance: requiredString("Member's insurance is required"),
  foodSafetyCertDate: z
    .string()
    .min(1, "Food safety cert. date is required")
    .refine(
      (val) => {
        const today = new Date().toISOString().slice(0, 10);
        return val <= today;
      },
      { message: "Food safety cert. date must be today or a past date" }
    ),
});

export type AgreementMemberInfoFormValues = z.infer<typeof agreementMemberInfoSchema>;

/** First validation error for agreement member info + signature, or null when complete. */
export function getAgreementMemberInfoValidationError(
  info: AgreementMemberInfo | null | undefined
): string | null {
  if (!info) return "Please complete all required fields.";
  const parsed = agreementMemberInfoSchema.safeParse(info);
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? "Please complete all required fields.";
  }
  if (!info.signatureDataUrl?.trim().startsWith("data:")) {
    return "Please add your signature before submitting.";
  }
  return null;
}

export function isAgreementMemberInfoComplete(info: AgreementMemberInfo | null | undefined): boolean {
  return getAgreementMemberInfoValidationError(info) === null;
}

export type AgreementMemberInfoInvalidField = keyof AgreementMemberInfo | "signature";

/** First invalid field key for scroll/highlight targeting, or null when complete. */
export function getFirstAgreementMemberInfoInvalidField(
  info: AgreementMemberInfo | null | undefined
): AgreementMemberInfoInvalidField | null {
  if (!info) return "fullName";
  const parsed = agreementMemberInfoSchema.safeParse(info);
  if (!parsed.success) {
    const path = parsed.error.issues[0]?.path[0];
    if (typeof path === "string") return path as keyof AgreementMemberInfo;
    return "fullName";
  }
  if (!info.signatureDataUrl?.trim().startsWith("data:")) {
    return "signature";
  }
  return null;
}
